import { api } from '@/api/client';

/**
 * Тумблеры ИИ-заданий по навыкам (12–13.10.2026, решение владельца).
 * Договор — docs/CONTRACTS/ai-skills.md, ручка `GET /ielts/ai-skills`.
 *
 * Сервер уже свёл всё: тумблер сервера (раздел флагов), тумблер курса (карточка
 * курса), тариф и сданный мок. Здесь ничего не пересчитывается: тренажёр навыка
 * открыт, только если ручка сказала `true`.
 *
 * Мок и спринт тумблеров не имеют. Мок — событие с датой (окно назначает
 * куратор); `mock_skills` — из чего соберётся ближайший мок, `mock_state` — где
 * ученик на пути: `not_scheduled` (окна нет), `open` (можно сдавать), `passed`.
 * Спринт открыт, если `sprint_skills` не пуст.
 *
 * ⚠️ ВНЕШНЕЕ ХРАНИЛИЩЕ, А НЕ ТОЛЬКО ЗАПРОС. Закрытые входы читаются синхронно —
 * `closedEntry` в `aiEntries.ts` зовут списки плиток, ссылки заданий, гейты
 * маршрутов. Экран, которому нужна перерисовка по приезде ответа, подписывается
 * через `useAiSkills()`.
 *
 * ⚠️ ЗАПРОС — ТОЛЬКО ПО ЯВНОМУ ВЫЗОВУ. Ни при загрузке модуля, ни при входе:
 * бесплатнику, у которого тренажёров нет по тарифу, ни одного лишнего запроса
 * (`useAiSkills` решает по тарифу, сторож — `aiSkills.test.ts`).
 *
 * ⚠️ ПОКА ОТВЕТА НЕТ — ЗАКРЫТО, НО НЕ «ЗАГРУЖЕНО». Плитки появятся по приезде;
 * гейт маршрута ждёт (`aiSkillsLoaded`). Ошибка ручки — закрыто и отдельный
 * признак `aiSkillsFailed`: вкладка приложения по ошибке не прячется.
 */

export type AiSkill = 'reading' | 'listening' | 'writing' | 'speaking';
export type MockState = 'not_scheduled' | 'open' | 'passed';

export type AiSkills = Record<AiSkill, boolean> & {
  /** Из чего соберётся ближайший мок, в порядке разделов. */
  mock_skills: string[];
  /** Из чего собирается спринт; пусто — спринта нет. */
  sprint_skills: string[];
  mock_state: MockState;
};

export const AI_SKILLS_CLOSED: AiSkills = {
  reading: false,
  listening: false,
  writing: false,
  speaking: false,
  mock_skills: [],
  sprint_skills: [],
  mock_state: 'not_scheduled',
};

/** Сколько держать ответ: столько же, сколько сервер держит тумблеры, ×2. */
export const AI_SKILLS_TTL_MS = 60_000;

/** Навыки, из которых бывает спринт (сервер: sprintSkills). */
const SPRINT_SKILLS: AiSkill[] = ['listening', 'reading', 'writing'];

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
}

/**
 * Разбор ответа. Строго `true` — открыто; всё остальное, включая незнакомые и
 * испорченные поля, — закрыто. Незнакомые ключи пропускаются молча (договор §0).
 */
export function parseAiSkills(raw: unknown): AiSkills {
  if (!raw || typeof raw !== 'object') return { ...AI_SKILLS_CLOSED, mock_skills: [], sprint_skills: [] };
  const r = raw as Record<string, unknown>;
  const on = (k: AiSkill) => r[k] === true;
  const state = r.mock_state === 'open' || r.mock_state === 'passed' ? r.mock_state : 'not_scheduled';
  return {
    reading: on('reading'),
    listening: on('listening'),
    writing: on('writing'),
    speaking: on('speaking'),
    // Пустой срез Go — null в JSON; массив нормализуем здесь, а не в экране.
    mock_skills: strings(r.mock_skills),
    sprint_skills: strings(r.sprint_skills),
    mock_state: state,
  };
}

/**
 * Навык включён, но ещё не сдан в моке: тренажёр показывается под замком
 * «сначала мок», а не прячется. Чистая функция — проверяется без хранилища.
 */
export function skillPending(s: AiSkills, skill: AiSkill): boolean {
  return !s[skill] && s.mock_state !== 'passed' && s.mock_skills.includes(skill);
}

/** Спринт ждёт мока: открытого спринта нет, а навык из него ждёт сдачи. */
export function sprintPending(s: AiSkills): boolean {
  return s.sprint_skills.length === 0 && SPRINT_SKILLS.some((k) => skillPending(s, k));
}

/** Есть ли у ученика хоть один включённый навык (открытый или ждущий мока). */
export function anySkillEnabled(s: AiSkills): boolean {
  return (['reading', 'listening', 'writing', 'speaking'] as AiSkill[]).some((k) => s[k] || skillPending(s, k));
}

type State = {
  userId: string | null;
  skills: AiSkills;
  loaded: boolean;
  failed: boolean;
  at: number;
  version: number;
};

let state: State = { userId: null, skills: AI_SKILLS_CLOSED, loaded: false, failed: false, at: 0, version: 0 };
let inflight: Promise<void> | null = null;
/** Сбоев подряд: автоповтор — только после первого (ensureAiSkills). */
let failStreak = 0;
/** Через сколько повторить запрос после первого сбоя. */
export const AI_SKILLS_RETRY_MS = 2_000;
/** Номер последнего запроса: ответ более раннего (прежняя учётка) выбрасывается. */
let requestSeq = 0;
const listeners = new Set<() => void>();

function set(next: Partial<State>) {
  state = { ...state, ...next, version: state.version + 1 };
  listeners.forEach((l) => l());
}

export function subscribeAiSkills(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getAiSkillsVersion(): number {
  return state.version;
}

export function getAiSkills(): AiSkills {
  return state.skills;
}

export function aiSkillsLoaded(): boolean {
  return state.loaded;
}

/** Ручка ответила ошибкой: всё закрыто, но прятать по этому ответу нельзя. */
export function aiSkillsFailed(): boolean {
  return state.failed;
}

export function aiSkillOn(skill: AiSkill): boolean {
  return state.skills[skill];
}

export function aiSkillWaitsForMock(skill: AiSkill): boolean {
  return skillPending(state.skills, skill);
}

export function aiSprintOn(): boolean {
  return state.skills.sprint_skills.length > 0;
}

export function aiSprintWaitsForMock(): boolean {
  return sprintPending(state.skills);
}

/**
 * Сбросить хранилище, только если в нём ответ ДРУГОЙ учётки (или вход сменился на
 * «никто»). Ответ своей учётки и её запрос в пути не трогаются. Для экранов, которым
 * ручка не нужна: им нельзя выбрасывать ответ, запрошенный соседним экраном.
 */
export function dropAiSkillsOfOtherUser(userId: string | null | undefined): void {
  const id = userId ?? null;
  if (state.userId === null || state.userId === id) return;
  inflight = null;
  requestSeq += 1;
  set({ userId: null, skills: AI_SKILLS_CLOSED, loaded: false, failed: false, at: 0 });
}

/**
 * Загрузить ответ для этой учётки, если его нет или он устарел.
 *
 * ⚠️ СМЕНА УЧЁТКИ СБРАСЫВАЕТ ОТВЕТ. Иначе после входа под другим учеником
 * минуту показывались бы чужие навыки.
 */
export function ensureAiSkills(userId: string | null | undefined, now: number = Date.now()): Promise<void> {
  const id = userId ?? null;
  if (id !== state.userId) {
    inflight = null;
    requestSeq += 1;
    set({ userId: id, skills: AI_SKILLS_CLOSED, loaded: false, failed: false, at: 0 });
  }
  if (!id) return Promise.resolve();
  // ⚠️ СБОЙ НЕ КЭШИРУЕТСЯ (20.10.2026, T5). Раньше неудачный запрос ложился в хранилище
  // «всё закрыто, загружено» на весь срок кэша, и экран мока минуту показывал «Mock test
  // ещё не назначен» с неактивной кнопкой, хотя сервер ответил бы «открыт» (стенд 20.10:
  // запрос не дошёл до шлюза — оборвался в браузере). Теперь следующий вызов переспрашивает.
  if (state.loaded && !state.failed && now - state.at < AI_SKILLS_TTL_MS) return Promise.resolve();
  if (inflight) return inflight;
  requestSeq += 1;
  const seq = requestSeq;
  inflight = api
    .get('/ielts/ai-skills')
    .then(({ data }) => ({ skills: parseAiSkills(data), failed: false }))
    .catch(() => ({ skills: AI_SKILLS_CLOSED, failed: true }))
    .then(({ skills, failed }) => {
      // Ответ для прежней учётки, пришедший после смены, — выбрасываем.
      if (seq !== requestSeq || state.userId !== id) return;
      inflight = null;
      failStreak = failed ? failStreak + 1 : 0;
      set({ skills, loaded: true, failed, at: Date.now() });
      // Один автоповтор на серию сбоев: разовый обрыв сети лечится сам, а лежащая
      // служба не получает запрос каждые две секунды.
      if (failed && failStreak === 1) {
        setTimeout(() => {
          if (state.userId === id && state.failed) void ensureAiSkills(id);
        }, AI_SKILLS_RETRY_MS);
      }
    });
  return inflight;
}

/**
 * Только для проверок. `null` — ответа ещё нет. Учётка по умолчанию `null` —
 * та же, что у проверки без входа, иначе `useAiSkills` сбросил бы подставленное.
 */
export function __setAiSkillsForTests(skills: Partial<AiSkills> | null, userId: string | null = null) {
  inflight = null;
  failStreak = 0;
  requestSeq += 1;
  state = {
    userId,
    skills: skills ? { ...AI_SKILLS_CLOSED, ...skills } : AI_SKILLS_CLOSED,
    loaded: skills !== null,
    failed: false,
    at: skills ? Date.now() : 0,
    version: state.version + 1,
  };
  listeners.forEach((l) => l());
}
